import { LeanClient } from 'types/client';
import { DivisionTree, SurveyGroup, SurveyType } from '@zencity/survey-types';
import { fetchSurveyGroups } from 'services/surveyGroup';
import { fetchRakeWeighterExecutions } from 'services/communitySurveyReport/rakeWighterExecutions';
import {
  DivisionTreeBySurveyType,
  RakeWeighterExecutionsBySurveyGroup,
  ReportBuilderClientData,
} from 'screens/WidgetGenerator/reportGeneratorComponents/types';
import { RakeWeighterExecution } from 'utils/rakeWeighterExecutions';
import { fetchDivisionsTree } from 'services/division';
import {
  buildBundleDataItemDefinitions,
  buildSingleWidgetDataItemDefinitions,
} from 'screens/WidgetGenerator/reportGeneratorComponents/dataItemDefinitions';
import { IDataItemDefinition } from '@zencity/reporting-types';
import { productPrefixDefinitionKey } from 'screens/WidgetGenerator/reportGeneratorComponents/helpers/dataItemDefinition';

const getClientSurveysGroups = async (zcClientId: string, surveyTypes: SurveyType[]): Promise<SurveyGroup[]> => {
  const clientSurveysGroups = await fetchSurveyGroups({
    zc_client_id: zcClientId,
    survey_type__in: surveyTypes.join(','),
  });

  if (clientSurveysGroups.length < 1) {
    throw new Error('Client should have a supported `SurveyGroup`');
  }

  return clientSurveysGroups;
};

const getRakeWeighterExecutionsBySurveyGroup = async (
  zcClientId: string,
  surveysGroups: SurveyGroup[],
): Promise<RakeWeighterExecutionsBySurveyGroup> => {
  const rakeWeighterExecutionsRequests: Promise<RakeWeighterExecution[]>[] = [];
  surveysGroups.forEach((surveyGroup) => {
    rakeWeighterExecutionsRequests.push(
      fetchRakeWeighterExecutions({
        survey_group: surveyGroup.id,
        zc_client_id: zcClientId,
        display_on_dashboard: true,
      }),
    );
  });

  const rakeWeighterExecutionsBySurveyGroup = {} as RakeWeighterExecutionsBySurveyGroup;
  const rakeWeighterExecutionsResponses = await Promise.all(rakeWeighterExecutionsRequests);
  rakeWeighterExecutionsResponses.forEach((rakeWeighterExecutions) => {
    if (rakeWeighterExecutions.length < 1) {
      return;
    }
    const surveyGroupId = rakeWeighterExecutions[0].survey_group;
    rakeWeighterExecutionsBySurveyGroup[surveyGroupId] = rakeWeighterExecutions;
  });

  return rakeWeighterExecutionsBySurveyGroup;
};

const getDivisionTreeBySurveyType = async (
  zcClientId: string,
  surveyTypes: SurveyType[],
): Promise<DivisionTreeBySurveyType> => {
  const divisionTreeRequests: Promise<{
    divisionTree: DivisionTree[];
    surveyType: SurveyType;
  }>[] = [];
  surveyTypes.forEach((surveyType) => {
    divisionTreeRequests.push(
      fetchDivisionsTree(surveyType, zcClientId).then((divisionTree) => ({
        divisionTree,
        surveyType,
      })),
    );
  });

  const divisionTreeBySurveyType = {} as DivisionTreeBySurveyType;
  const divisionTreeResponses = await Promise.all(divisionTreeRequests);
  divisionTreeResponses.forEach(({ divisionTree, surveyType }) => {
    if (divisionTree.length < 1) {
      return;
    }

    divisionTreeBySurveyType[surveyType] = divisionTree;
  });

  return divisionTreeBySurveyType;
};

export const getRequiredSurveysDataReportBuilder = async (zcClientId: string): Promise<ReportBuilderClientData> => {
  const supportedSurveyTypes = [SurveyType.COMMUNITY_SURVEY, SurveyType.BLOCKWISE, SurveyType.PULSE];

  const clientSurveysGroups = await getClientSurveysGroups(zcClientId, supportedSurveyTypes);
  const clientSurveyGroupTypes = clientSurveysGroups
    .filter((surveyGroup) => supportedSurveyTypes.includes(surveyGroup.type))
    .map((surveyGroup) => surveyGroup.type);
  const clientSurveyTypes = Array.from(new Set(clientSurveyGroupTypes));
  const rakeWeighterExecutionsBySurveyGroup = await getRakeWeighterExecutionsBySurveyGroup(
    zcClientId,
    clientSurveysGroups,
  );
  const divisionTreeBySurveyType = await getDivisionTreeBySurveyType(zcClientId, clientSurveyTypes);

  const { client } = clientSurveysGroups[0];
  const leanClient: LeanClient = {
    id: client.id,
    name: client.name,
    zc_client_id: client.zc_client_id,
    state: client.state || undefined,
  };
  return {
    client: leanClient,
    surveysGroups: clientSurveysGroups,
    rakeWeighterExecutionsBySurveyGroup,
    divisionTreeBySurveyType,
  };
};

const prefixDataItemDefinitionKey = (definition: IDataItemDefinition): IDataItemDefinition => ({
  ...definition,
  key: productPrefixDefinitionKey(definition.key, definition.product),
});

export const buildDataItemDefinitions = (
  isSuperAdmin: boolean,
  clientData: ReportBuilderClientData,
): IDataItemDefinition[] => {
  const singleWidgetDataItemDefinitions = buildSingleWidgetDataItemDefinitions(isSuperAdmin, clientData);
  const bundleItemDefinitions = buildBundleDataItemDefinitions(isSuperAdmin);

  return [...singleWidgetDataItemDefinitions, ...bundleItemDefinitions]
    .sort((itemA, itemB) =>
      // Sort the widgets alphabetically for easier navigation in the report
      // builder.
      itemA.name > itemB.name ? 1 : -1,
    )
    .map(prefixDataItemDefinitionKey);
};
